import { z } from "zod";

/**
 * The way back out of a workspace to the page that opened it: the Workspaces page's
 * search ("Åbn workspace", LAR-235) or the orphaned-workspaces list.
 *
 * The opener hands it over in the router state. The workspace then keeps it in
 * `sessionStorage` for as long as the reader stays inside the workspace and its
 * documents: moving between folders and tabs drops the router state, and so does the
 * document viewer's "Tilbage", which navigates to the workspace afresh. Leaving the
 * workspace any other way drops it (see {@link forgetWorkspaceReturnOutside}), so a
 * later visit never offers a stale way back.
 */
export type WorkspaceReturn = {
  /** In-app path to go back to, query string included. */
  returnTo: string;
  /** What the back button says, e.g. "Tilbage til søgningen". */
  label: string;
};

export const WORKSPACE_RETURN_STORAGE_KEY = "workspaceReturn";

const workspaceReturnSchema = z.object({
  returnTo: z.string().startsWith("/"),
  label: z.string().min(1),
});

const routerStateSchema = z.object({ workspaceReturn: workspaceReturnSchema });

const storedSchema = workspaceReturnSchema.extend({
  workspaceId: z.string().min(1),
});

/** Router state that opens a workspace with a way back to `returnTo`. */
export const workspaceReturnState = (returnTo: string, label: string) => ({
  workspaceReturn: { returnTo, label } satisfies WorkspaceReturn,
});

/**
 * Router state for a workspace opened from the Workspaces page's search, by "Åbn
 * workspace" or by its row: its button leads back to the search at `searchPath`.
 */
export const backToWorkspaceSearchState = (searchPath: string) =>
  workspaceReturnState(searchPath, "Tilbage til søgningen");

/** The way back handed over in the router state, if there is one. */
export const workspaceReturnFromState = (
  state: unknown
): WorkspaceReturn | undefined => {
  const parsed = routerStateSchema.safeParse(state);
  return parsed.success ? parsed.data.workspaceReturn : undefined;
};

const readStored = () => {
  if (typeof sessionStorage === "undefined") return undefined;
  const stored = sessionStorage.getItem(WORKSPACE_RETURN_STORAGE_KEY);
  if (!stored) return undefined;
  try {
    const parsed = storedSchema.safeParse(JSON.parse(stored));
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
};

/** Keeps the way back for `workspaceId`, replacing any other workspace's. */
export const rememberWorkspaceReturn = (
  workspaceId: string,
  workspaceReturn: WorkspaceReturn
): void => {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.setItem(
    WORKSPACE_RETURN_STORAGE_KEY,
    JSON.stringify({ workspaceId, ...workspaceReturn })
  );
};

/** The way back kept for `workspaceId`, if the reader has not left it since. */
export const recallWorkspaceReturn = (
  workspaceId: string
): WorkspaceReturn | undefined => {
  const stored = readStored();
  if (stored?.workspaceId !== workspaceId) return undefined;
  return { returnTo: stored.returnTo, label: stored.label };
};

/**
 * Drops the way back once the reader is outside the workspace it belongs to. The
 * workspace's tabs and folders live under `/workspaces/<id>/`, and so do its documents.
 */
export const forgetWorkspaceReturnOutside = (pathname: string): void => {
  const stored = readStored();
  if (!stored) return;
  const workspacePath = `/workspaces/${stored.workspaceId}`;
  if (pathname === workspacePath || pathname.startsWith(`${workspacePath}/`)) {
    return;
  }
  sessionStorage.removeItem(WORKSPACE_RETURN_STORAGE_KEY);
};
